import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { D, designError, fmtDate, fmtDateTime, label, useDesignMe } from "@/lib/designApi";
import { DataTable, ErrorList, Field, FilesPanel, NoAccess, PageHeader, Section, SignDialog, SimpleSelect, StatusBadge, useQueryId } from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Flag, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

function StageCard({ project, stage, idx, onChanged }) {
  const { me, can } = useDesignMe();
  const [dl, setDl] = useState(stage.deliverables || []);
  const [text, setText] = useState("");
  const [gate, setGate] = useState(false);
  const [decision, setDecision] = useState("passed");
  useEffect(() => setDl(stage.deliverables || []), [stage.deliverables]);
  const locked = ["passed", "conditional"].includes(stage.status) || project.status !== "active";
  const prevOk = idx === 0 || ["passed", "conditional"].includes(project.stages[idx - 1].status);
  const save = async (patch) => {
    try { await api.patch(`${D}/npd/${project.id}/stages/${stage.key}`, patch); onChanged(); } catch (e) { toast.error(designError(e)); }
  };
  return (
    <div className={`border rounded-lg p-3 bg-white ${stage.status === "in_progress" ? "border-blue-300 shadow-sm" : "border-slate-200"}`} data-testid={`npd-stage-${stage.key}`}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-slate-100 text-xs font-bold flex items-center justify-center">{stage.seq}</span>
        <div className="font-semibold">{stage.name}</div>
        <StatusBadge status={stage.status} />
        <div className="flex-1" />
        <span className="text-xs text-slate-500"><Flag className="w-3 h-3 inline mr-1" />{stage.gate}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Field label="Planned start"><Input type="date" value={stage.planned_start || ""} disabled={locked || !can("npd_manage")} onChange={(e) => save({ planned_start: e.target.value || null })} /></Field>
        <Field label="Planned end"><Input type="date" value={stage.planned_end || ""} disabled={locked || !can("npd_manage")} onChange={(e) => save({ planned_end: e.target.value || null })} /></Field>
      </div>
      <div className="mt-2 space-y-1">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Deliverables</div>
        {dl.map((d, i) => (
          <div key={d.id || i} className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!d.done} disabled={locked || !can("npd_manage")} onCheckedChange={(v) => save({ deliverables: dl.map((x, j) => (j === i ? { ...x, done: !!v } : x)) })} />
            <span className={d.done ? "line-through text-slate-500" : ""}>{d.text}</span>
            {d.done_by && <span className="text-[10px] text-slate-400">{d.done_by} · {fmtDate(d.done_at)}</span>}
            {!locked && can("npd_manage") && <button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => save({ deliverables: dl.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {!locked && can("npd_manage") && (
          <div className="flex gap-2"><Input placeholder="Add deliverable" value={text} onChange={(e) => setText(e.target.value)} />
            <Button size="sm" variant="outline" disabled={!text.trim()} onClick={() => { save({ deliverables: [...dl, { text, done: false }] }); setText(""); }}>Add</Button></div>
        )}
      </div>
      {stage.gate_decision && <div className="text-xs mt-2 text-slate-600">Gate: <b>{label(stage.gate_decision)}</b> by {stage.gate_by_name || stage.gate_by} · {fmtDateTime(stage.gate_at)} {stage.gate_comment && `· ${stage.gate_comment}`}</div>}
      {!locked && prevOk && can("npd_gate") && (
        <div className="mt-2 flex justify-end"><Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setGate(true)} data-testid={`npd-gate-${stage.key}`}>Gate review</Button></div>
      )}
      {gate && (
        <SignDialog open onOpenChange={setGate} title={`${stage.gate}`} meaning="Gate decision is e-signed. 'Passed' requires all deliverables done."
          requireComment={decision !== "passed"} requirePassword={me?.config?.esign_require_password !== false}
          onSubmit={async ({ password, comment }) => { await api.post(`${D}/npd/${project.id}/stages/${stage.key}/gate`, { decision, comment, password }); toast.success("Gate recorded"); onChanged(); }}>
          <Field label="Decision"><SimpleSelect value={decision} onChange={setDecision} options={me?.enums?.gate_decisions || []} /></Field>
        </SignDialog>
      )}
    </div>
  );
}

function NpdDetail({ id, onChanged }) {
  const { can } = useDesignMe();
  const [p, setP] = useState(null);
  const load = useCallback(() => api.get(`${D}/npd/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(designError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  const changed = () => { load(); onChanged?.(); };
  const setStatus = async (status) => {
    try { await api.patch(`${D}/npd/${id}`, { status }); changed(); } catch (e) { toast.error(designError(e)); }
  };
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500 mono">{p.number} · owner {p.owner_email} · target {fmtDate(p.target_date)}</div>
        <div className="text-xl font-bold text-slate-900">{p.name}</div>
        <div className="flex items-center gap-3 mt-2 max-md:flex-wrap"><StatusBadge status={p.status} /><div className="w-64 max-md:w-full"><Progress value={p.progress_pct} /></div><span className="text-sm">{p.progress_pct}% · {p.current_stage}</span>
          {p.overdue && <span className="text-xs text-red-600 font-semibold">PAST TARGET</span>}
          <div className="flex-1" />
          {can("npd_manage") && p.status === "active" && <Button size="sm" variant="outline" onClick={() => setStatus("on_hold")}>Put on hold</Button>}
          {can("npd_manage") && p.status === "on_hold" && <Button size="sm" variant="outline" onClick={() => setStatus("active")}>Resume</Button>}
          {can("npd_gate") && ["active", "on_hold"].includes(p.status) && <Button size="sm" variant="outline" className="text-red-600" onClick={() => window.confirm("Cancel this project?") && setStatus("cancelled")}>Cancel</Button>}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {p.stages.map((s, i) => <StageCard key={s.key} project={p} stage={s} idx={i} onChanged={changed} />)}
      </div>
      <Section title={`Linked parts (${p.items?.length || 0})`}>
        {(p.items || []).map((it) => <div key={it.id} className="text-sm"><span className="mono">{it.number} Rev {it.revision}</span> {it.name} <StatusBadge status={it.status} /></div>)}
        {!p.items?.length && <div className="text-xs text-slate-500">Link parts by setting the NPD project on new items, or list part numbers here.</div>}
      </Section>
      <FilesPanel ownerType="npd" ownerId={p.id} files={p.files} editable={p.status === "active" && can("npd_manage")} onChanged={load} title="Project documents" />
    </div>
  );
}

export default function DesignNpd() {
  const { me, can, error: meErr } = useDesignMe();
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(useQueryId());
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", customer_name: "", target_date: "" });
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => api.get(`${D}/npd`).then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  const create = async () => {
    setErrors([]);
    try {
      const { data } = await api.post(`${D}/npd`, { ...form, target_date: form.target_date || null });
      setCreating(false); load(); setOpenId(data.id);
    } catch (e) { setErrors([designError(e)]); }
  };
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-npd-page">
      <PageHeader title="NPD Projects" subtitle="New product development — stages, deliverables and e-signed gate reviews">
        {can("npd_manage") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="design-new-npd"><Plus className="w-4 h-4 mr-1.5" />New project</Button>}
      </PageHeader>
      <DataTable rows={rows} onRowClick={(r) => setOpenId(r.id)} testid="design-npd-table"
        columns={[
          { key: "number", title: "Project", cellClass: "mono font-semibold" },
          { key: "name", title: "Name", cellClass: "max-w-xs truncate" },
          { key: "customer_name", title: "Customer" },
          { key: "current_stage", title: "Current stage" },
          { key: "progress_pct", title: "Progress", render: (r) => <div className="w-32 flex items-center gap-2"><Progress value={r.progress_pct} /><span className="text-xs mono">{r.progress_pct}%</span></div> },
          { key: "target_date", title: "Target", render: (r) => <span className={r.overdue ? "text-red-600 font-semibold" : ""}>{fmtDate(r.target_date)}</span> },
          { key: "late_stages", title: "Late stages", cellClass: "mono" },
          { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} /> },
        ]} />
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>New NPD project</DialogTitle></DialogHeader>
          <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="npd-name" /></Field>
          <Field label="Customer"><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></Field>
          <Field label="Target launch date"><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></Field>
          <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <ErrorList errors={errors} />
          <DialogFooter><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={form.name.length < 3} onClick={create}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto">
          <SheetHeader><SheetTitle>NPD project</SheetTitle></SheetHeader>
          {openId && <NpdDetail key={openId} id={openId} onChanged={load} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
